// Archivo que maneja el panel de control
//

import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import {
  Assessment,
  Dashboard,
  Draw,
  Groups,
  Preview,
  Security,
  Settings as SettingsIcon,
  Tune,
  UploadFile,
  ViewModule,
  Widgets,
} from '@mui/icons-material'
import { matchPath, Outlet, useLocation, useNavigate } from 'react-router-dom'
import api from '../../services/api'
import AdminConsoleShell, {
  AdminHero,
  AdminModuleCard,
  AdminSectionCard,
} from '../../components/settings/AdminConsoleShell'
import OperationalInsightsPanel from '../../components/settings/OperationalInsightsPanel'
import WorkflowWidgetPreconfigPanel from '../../components/settings/WorkflowWidgetPreconfigPanel'
import {
  DEFAULT_WORKFLOW_WIDGET_CATALOG,
  readWorkflowWidgetCatalog,
  resetWorkflowWidgetCatalog,
  saveWorkflowWidgetCatalog,
} from './workflowWidgetCatalog'

const ControlPanel = () => {
  const navigate = useNavigate()
  const location = useLocation()

  const [products, setProducts] = useState([])
  const [productId, setProductId] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [isGlobalAdmin, setIsGlobalAdmin] = useState(false)
  const [sessionUser, setSessionUser] = useState(null)
  const [catalogSaved, setCatalogSaved] = useState('')
  const [workflowWidgetCatalog, setWorkflowWidgetCatalog] = useState(
    readWorkflowWidgetCatalog
  )

  const safeProducts = useMemo(
    () => (Array.isArray(products) ? products : []),
    [products]
  )

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true)
      setError('')
      try {
        const res = await api.get('/products')
        const data = res?.data
        const list = Array.isArray(data) ? data : data?.products
        setProducts(Array.isArray(list) ? list : [])
      } catch (e) {
        console.error('[ControlPanel] Error fetching products:', e)
        setProducts([])
        setError('No se pudieron cargar los productos.')
      } finally {
        setLoading(false)
      }
    }

    fetchProducts()
  }, [])

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const res = await api.get('/auth/me')
        const user = res?.data?.user ?? res?.data
        const roles = Array.isArray(user?.roles) ? user.roles : []
        const hasGlobalAdmin = roles.some((r) => {
          const type = typeof r === 'string' ? r : r?.type
          const name = typeof r === 'string' ? r : r?.name_role
          return type === 'globalAdmin' || name === 'globalAdmin'
        })
        setSessionUser(user || null)
        setIsGlobalAdmin(Boolean(hasGlobalAdmin))
      } catch (e) {
        setSessionUser(null)
        setIsGlobalAdmin(false)
      }
    }

    fetchCurrentUser()
  }, [])

  const handleGoToManager = () => {
    if (!productId) return
    navigate(`products/${productId}/layouts`)
  }

  const handleNavigateToProduct = (selectedProductId) => {
    if (!selectedProductId) return
    navigate(`products/${selectedProductId}/layouts`)
  }

  const handleGoToLayoutEditor = () => {
    if (!productId) {
      if (isGlobalAdmin) {
        navigate('/settings/controlpanel/layout-manager')
        return
      }
      setError(
        'Debes seleccionar primero un producto para abrir el editor de layouts.'
      )
      return
    }
    setError('')
    navigate(`/settings/controlpanel/products/${productId}/layout-editor`)
  }

  const isLayoutEditorRoute = location.pathname.includes('/layout-editor')

  const menuItems = [
    {
      label: 'Resumen administrativo',
      description:
        'Acceso rápido al hub de configuración y herramientas clave.',
      icon: <Dashboard fontSize="small" />,
      path: '/settings/controlpanel',
      onClick: () => navigate('/settings/controlpanel'),
    },
    {
      label: 'Contexto operativo',
      description:
        'Selección oficial de cliente y producto para operación transversal.',
      icon: <Tune fontSize="small" />,
      path: '/settings/controlpanel/operational-context',
      onClick: () => navigate('/settings/controlpanel/operational-context'),
    },
    {
      label: 'Layouts por producto',
      description: 'Asignación, activación y vista previa operacional.',
      icon: <ViewModule fontSize="small" />,
      path: '/settings/controlpanel/layout-manager',
      onClick: () => navigate('/settings/controlpanel/layout-manager'),
    },
    {
      label: 'Editor de layouts',
      description: 'Workspace visual para construir y mantener layouts.',
      icon: <Tune fontSize="small" />,
      path: '/settings/controlpanel/products/:productId/layout-editor',
      onClick: handleGoToLayoutEditor,
    },
    {
      label: 'Códigos y catálogos',
      description:
        'Administración de catálogos maestros consumidos por layouts y flujos operativos.',
      icon: <SettingsIcon fontSize="small" />,
      path: '/settings/controlpanel/codes-management',
      onClick: () => navigate('/settings/controlpanel/codes-management'),
    },
    {
      label: 'Usuarios',
      description: 'Acceso a la administración de cuentas internas.',
      icon: <Groups fontSize="small" />,
      path: '/settings/controlpanel/users',
      onClick: () => navigate('/settings/controlpanel/users'),
    },
    {
      label: 'Roles y privilegios',
      description: 'Control centralizado de permisos y privilegios.',
      icon: <Security fontSize="small" />,
      path: '/settings/controlpanel/roles',
      onClick: () => navigate('/settings/controlpanel/roles'),
    },
    {
      label: 'Cargas y auditoría',
      description:
        'Operaciones masivas, revisiones y utilidades administrativas.',
      icon: <UploadFile fontSize="small" />,
      path: '/settings/controlpanel/uploads',
      onClick: () => navigate('/settings/controlpanel/uploads'),
    },
    {
      label: 'Workflow widgets',
      description:
        'Preconfiguración no-code de presets y catálogos para workflow_stage_banner.',
      icon: <Widgets fontSize="small" />,
      path: '/settings/workflow-widgets',
      onClick: () => navigate('/settings/workflow-widgets'),
    },
  ]

  const isOverview = Boolean(matchPath({ path: '/settings/controlpanel', end: true }, location.pathname))

  const workflowPresetRows = useMemo(() => {
    const presets = workflowWidgetCatalog?.workflow_stage_banner?.presets
    return Array.isArray(presets) ? presets : []
  }, [workflowWidgetCatalog])

  const moduleCards = [
    {
      title: 'Contexto operativo',
      description:
        'Define cliente y producto activos antes de operar módulos sensibles.',
      meta: 'Punto oficial de activación para globalAdmin.',
      icon: <Tune />,
      primaryAction: (
        <Button
          variant="contained"
          onClick={() => navigate('/settings/controlpanel/operational-context')}
        >
          Definir contexto
        </Button>
      ),
      secondaryAction: (
        <Button
          variant="outlined"
          onClick={() => navigate('/settings/controlpanel/layout-manager')}
        >
          Ir a layouts
        </Button>
      ),
    },
    {
      title: 'Administración de layouts',
      description:
        'Coordina qué layout está disponible o activo por producto sin alterar contratos de backend.',
      meta: 'Operación empresarial sobre layouts aplicados por producto.',
      icon: <Widgets />,
      primaryAction: (
        <Button
          variant="contained"
          onClick={() => navigate('/settings/controlpanel/layout-manager')}
        >
          Abrir módulo
        </Button>
      ),
      secondaryAction: (
        <Button
          variant="outlined"
          onClick={handleGoToLayoutEditor}
        >
          Ir al editor
        </Button>
      ),
    },
    {
      title: 'Editor low-code de layouts',
      description:
        'Experiencia de builder con biblioteca de campos, canvas y propiedades para mantener el motor actual.',
      meta: 'Inspirado en workspace visual tipo PowerApps.',
      icon: <Draw />,
      primaryAction: (
        <Button
          variant="contained"
          onClick={handleGoToLayoutEditor}
        >
          Diseñar layout
        </Button>
      ),
      secondaryAction: (
        <Button
          variant="outlined"
          onClick={() => navigate('/settings/controlpanel/layout-manager')}
        >
          Ver asignaciones
        </Button>
      ),
    },
    {
      title: 'Códigos operativos',
      description:
        'Administra catálogos usados en flujos de acción, resultado y contacto con una presentación más clara.',
      meta: 'Catálogos críticos para operación y reporting.',
      icon: <Assessment />,
      primaryAction: (
        <Button
          variant="contained"
          onClick={() => navigate('/settings/controlpanel/codes-management')}
        >
          Gestionar códigos
        </Button>
      ),
      secondaryAction: (
        <Button
          variant="outlined"
          onClick={() => navigate('/settings/controlpanel/codes-management')}
        >
          Abrir catálogo
        </Button>
      ),
    },
    {
      title: 'Creación de consumidores',
      description:
        'Mantiene accesibles los flujos de creación guiada y carga masiva cuando forman parte del dominio administrativo.',
      meta: 'Herramientas auxiliares conectadas al panel de control.',
      icon: <Preview />,
      primaryAction: (
        <Button
          variant="contained"
          onClick={() => navigate('/settings/consumers/create')}
        >
          Abrir wizard
        </Button>
      ),
      secondaryAction: (
        <Button
          variant="outlined"
          onClick={() => navigate('/settings/consumers/bulk')}
        >
          Carga masiva
        </Button>
      ),
    },
  ]

  if (isLayoutEditorRoute) {
    return (
      <Box
        sx={{
          height: '100%',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Outlet />
      </Box>
    )
  }

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
      <AdminConsoleShell
        navigation={menuItems}
        currentPath={location.pathname}
        sidebarFooter={
          <Stack spacing={1}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Estado del dominio
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Productos accesibles: {safeProducts.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Rutas y permisos existentes se conservan sin cambios de contrato.
            </Typography>
          </Stack>
        }
      >
        <AdminHero
          eyebrow="Enterprise administration"
          title="Control Panel"
          description="Centro administrativo para configuración, layouts y operaciones de soporte. La reconstrucción se mantiene dentro del dominio de Settings para reforzar jerarquía visual sin alterar lógica existente."
          stats={[
            { label: 'Módulos clave', value: moduleCards.length },
            { label: 'Productos detectados', value: safeProducts.length },
            {
              label: 'Estado',
              value: loading ? 'Cargando' : 'Operativo',
              color: loading ? 'warning' : 'success',
            },
          ]}
          actions={
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
              <Button
                variant="contained"
                onClick={() =>
                  navigate('/settings/controlpanel/layout-manager')
                }
              >
                Administrar layouts
              </Button>
              <Button
                variant="outlined"
                onClick={handleGoToLayoutEditor}
              >
                Abrir builder
              </Button>
            </Stack>
          }
        />

        {isGlobalAdmin && !sessionUser?.productId && (
          <Alert
            severity="info"
            sx={{ mb: 3 }}
            action={
              <Button
                size="small"
                variant="contained"
                onClick={() => navigate('/settings/controlpanel/operational-context')}
              >
                Definir contexto
              </Button>
            }
          >
            Tu sesión globalAdmin aún no tiene contexto operativo activo.
          </Alert>
        )}

        {isOverview ? (
          <>
            <AdminSectionCard
              title="Acceso contextual por producto"
              subtitle="Mantén el flujo existente para abrir la administración de layouts directamente sobre un producto específico."
            >
              {loading && (
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <CircularProgress size={22} />
                  <Typography variant="body2">Cargando productos…</Typography>
                </Stack>
              )}

              {!loading && error && <Alert severity="error">{error}</Alert>}

              {!loading && !error && (
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={2}
                  alignItems={{ xs: 'stretch', md: 'center' }}
                >
                  <TextField
                    select
                    label="Producto"
                    value={productId}
                    onChange={(e) => setProductId(e.target.value)}
                    sx={{ minWidth: { xs: '100%', md: 320 } }}
                  >
                    {safeProducts.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.name_product}
                      </MenuItem>
                    ))}
                  </TextField>

                  <Button
                    variant="contained"
                    disabled={!productId}
                    onClick={handleGoToManager}
                  >
                    Abrir administración del producto
                  </Button>
                </Stack>
              )}
            </AdminSectionCard>

            <OperationalInsightsPanel
              products={safeProducts}
              onNavigateProduct={handleNavigateToProduct}
            />

            <AdminSectionCard
              title="Módulos administrativos"
              subtitle="Agrupación estilo consola empresarial para navegación, mantenimiento y acceso operativo rápido."
            >
              <Grid container spacing={2.5}>
                {moduleCards.map((card) => (
                  <Grid item xs={12} md={6} key={card.title}>
                    <AdminModuleCard {...card} />
                  </Grid>
                ))}
              </Grid>
            </AdminSectionCard>

            <WorkflowWidgetPreconfigPanel
              workflowWidgetCatalog={workflowWidgetCatalog}
              workflowPresetRows={workflowPresetRows}
              catalogSaved={catalogSaved}
              onChangeDefaultPreset={(nextDefaultPreset) => {
                setWorkflowWidgetCatalog((prev) => ({
                  ...prev,
                  workflow_stage_banner: {
                    ...(prev?.workflow_stage_banner || {}),
                    defaultPreset: nextDefaultPreset,
                  },
                }))
                setCatalogSaved('')
              }}
              onSaveCatalog={() => {
                saveWorkflowWidgetCatalog(workflowWidgetCatalog)
                setCatalogSaved('Catálogo guardado localmente en este navegador.')
              }}
              onResetCatalog={() => {
                resetWorkflowWidgetCatalog()
                setWorkflowWidgetCatalog(DEFAULT_WORKFLOW_WIDGET_CATALOG)
                setCatalogSaved('Catálogo restablecido a valores por defecto.')
              }}
            />
          </>
        ) : (
          <Outlet />
        )}
      </AdminConsoleShell>
    </Box>
  )
}

export default ControlPanel
